import { X, RotateCcw, CheckCircle, AlertCircle, Zap, Film, Music, FileText } from 'lucide-react'
import { useState } from 'react'
import { Badge, ProgressBar, Spinner } from '@/components/ui'
import { ChallengeSheet } from './ChallengeSheet'
import { cn } from '@/components/ui/utils'
import type { UploadQueueItem } from '@/types'

interface QueueItemCardProps {
  item:      UploadQueueItem
  onRetry:   (item: UploadQueueItem) => void
  onDismiss: (id: string) => void
  onCancel:  (id: string) => void
  eventName?: string
}

const STAGE_LABEL: Record<string, string> = {
  queued:    'Waiting…',
  uploading: 'Uploading',
  saving:    'Saving',
  routing:   'AI evaluating…',   // photo: vision model · video/audio/doc: context analysis
  retrying:  'Retrying…',
  done:      'In The Room',
  error:     'Failed',
}

const GRADE_STYLE: Record<string, string> = {
  A: 'bg-emerald-900/60 text-emerald-300',
  B: 'bg-blue-900/60 text-blue-300',
  C: 'bg-amber-900/60 text-amber-300',
  D: 'bg-red-900/60 text-red-300',
}

export function QueueItemCard({ item, onRetry, onDismiss, onCancel, eventName }: QueueItemCardProps) {
  const { status, file, progress, result, error, retries } = item

  // Local state for challenge revision — overrides result display
  const [revisedGrade, setRevisedGrade] = useState<string | null>(null)
  const [revisedScore, setRevisedScore] = useState<number | null>(null)
  const [revisedNote,  setRevisedNote]  = useState<string | null>(null)

  const displayGrade = revisedGrade ?? result?.grade
  const displayScore = revisedScore ?? result?.routeScore
  const displayNote  = revisedNote  ?? result?.editorNote

  return (
    <div className={cn(
      'rounded-xl border p-4 space-y-3 animate-slide-up transition-colors',
      status === 'done'  ? 'border-emerald-800/40 bg-emerald-950/20' :
      status === 'error' ? 'border-room-error/30 bg-red-950/20' :
      'border-room-border bg-room-surface'
    )}>
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          {status === 'done'  && <CheckCircle size={18} className="text-room-success" />}
          {status === 'error' && <AlertCircle size={18} className="text-room-error" />}
          {['uploading','saving','routing','retrying','queued'].includes(status) && <Spinner size={18} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {file.type.startsWith('video/') && <Film size={12} className="text-room-muted flex-shrink-0" />}
            {file.type.startsWith('audio/') && <Music size={12} className="text-room-muted flex-shrink-0" />}
            {!file.type.startsWith('image/') && !file.type.startsWith('video/') && !file.type.startsWith('audio/') && (
              <FileText size={12} className="text-room-muted flex-shrink-0" />
            )}
            <p className="text-white text-sm font-medium truncate">{file.name}</p>
          </div>
          <p className="text-room-muted text-xs mt-0.5">{STAGE_LABEL[status]}</p>
          {retries > 0 && status !== 'done' && (
            <p className="text-room-warning text-xs mt-0.5">Attempt {retries + 1} of 3</p>
          )}
        </div>

        <div className="flex gap-1 shrink-0">
          {status === 'error' && (
            <button onClick={() => onRetry(item)}
              className="p-1.5 rounded-lg text-room-muted hover:text-room-text hover:bg-room-border transition-colors"
              title="Retry">
              <RotateCcw size={14} />
            </button>
          )}
          {['uploading','saving','routing'].includes(status) && (
            <button onClick={() => onCancel(item.id)}
              className="p-1.5 rounded-lg text-room-muted hover:text-room-error transition-colors"
              title="Cancel">
              <X size={14} />
            </button>
          )}
          {(status === 'done' || status === 'error') && (
            <button onClick={() => onDismiss(item.id)}
              className="p-1.5 rounded-lg text-room-muted hover:text-room-text transition-colors"
              title="Dismiss">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {status === 'uploading' && progress > 0 && <ProgressBar value={progress} />}

      {/* AI result */}
      {status === 'done' && result && (
        <div className="space-y-2 pt-1 border-t border-room-border">
          {/* Grade + score */}
          <div className="flex items-center gap-2">
            {displayGrade && (
              <span className={cn('text-xs font-black px-2 py-0.5 rounded-lg', GRADE_STYLE[displayGrade] ?? GRADE_STYLE.D)}>
                Grade {displayGrade}
              </span>
            )}
            {revisedGrade && revisedGrade !== result.grade && (
              <span className="text-[10px] text-room-muted line-through">was {result.grade}</span>
            )}
            <span className="flex items-center gap-1 text-xs text-room-gold">
              <Zap size={10} />
              {Math.round((displayScore ?? 0) * 100)}%
            </span>
            <span className="text-room-muted text-xs capitalize">{result.aiMood}</span>
          </div>

          {/* Editor note */}
          {displayNote && (
            <p className="text-xs text-neutral-300 leading-relaxed italic border-l-2 border-room-accent/40 pl-2">
              {displayNote}
            </p>
          )}

          <p className="text-xs text-room-muted leading-relaxed">{result.aiDescription}</p>

          <div className="flex flex-wrap gap-1.5">
            {result.aiTags.slice(0, 5).map(t => (
              <Badge key={t} variant="accent">{t}</Badge>
            ))}
          </div>

          {/* Challenge — only for C/D grades */}
          <ChallengeSheet
            mediaItemId={result.mediaItemId}
            cloudinaryUrl={result.cloudinaryUrl}
            mediaType={file.type.startsWith('video') ? 'video' : 'photo'}
            fileName={file.name}
            eventName={eventName}
            originalGrade={result.grade ?? 'D'}
            originalScore={result.routeScore ?? 0}
            originalNote={result.editorNote ?? ''}
            onRevised={({ grade, score, note }) => {
              setRevisedGrade(grade)
              setRevisedScore(score)
              setRevisedNote(note)
            }}
          />
        </div>
      )}

      {/* Error detail */}
      {status === 'error' && error && (
        <p className="text-xs text-room-error px-1">{error}</p>
      )}
    </div>
  )
}
